import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { useDocsIndex } from '../lib/docsIndex'
import { listOf, type TreeDoc, type TreeTopic } from '../lib/types'
import { Spinner, cx } from './ui'

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { tree, loading, error } = useDocsIndex()

  if (loading) return <div className="p-4"><Spinner label="Loading nav…" /></div>
  if (error) return <p className="p-4 text-sm text-danger">Could not load the navigation.</p>
  if (!tree.length) return <p className="p-4 text-sm text-subtle">No documents yet.</p>

  return (
    <nav aria-label="Documentation" className="space-y-2 py-6 pr-3 pl-1">
      {tree.map((service) => (
        // The service is the loudest thing in the rail: a rule above it, and the
        // only bold, full-contrast label. Everything nested reads quieter, so depth
        // is legible at a glance rather than inverted.
        <section
          key={service.slug}
          className="border-t border-line pt-4 first:border-t-0 first:pt-0"
        >
          <h2 className="mb-2 px-2 text-[13px] font-bold tracking-tight text-ink">
            {service.title}
          </h2>

          {/* Docs pinned straight to the service, before any topic. */}
          {listOf(service.docs).map((doc) => (
            <DocLink key={doc.id} doc={doc} depth={0} onNavigate={onNavigate} />
          ))}


          {listOf(service.topics).map((topic) => (
            <Topic key={topic.slug} topic={topic} depth={0} onNavigate={onNavigate} />
          ))}
        </section>
      ))}
    </nav>
  )
}

/** Topics nest without limit; each level indents and collapses on its own. */
function Topic({
  topic,
  depth,
  onNavigate,
}: {
  topic: TreeTopic
  depth: number
  onNavigate?: () => void
}) {
  // Deep branches start folded — an expanded five-level tree is unreadable.
  const [open, setOpen] = useState(depth < 2)

  const children = listOf(topic.topics)
  const docs = listOf(topic.docs)
  if (!children.length && !docs.length) return null

  return (
    <div className="mb-0.5">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        className={cx(
          'flex w-full items-start gap-1.5 rounded-[var(--radius-sh)] py-1.5 pr-2 text-left text-[13px] hover:bg-sunken',
          // Nested branches step down in weight and contrast, never up.
          depth === 0 ? 'font-medium text-muted' : 'font-normal text-subtle',
        )}
      >
        <svg
          viewBox="0 0 24 24"
          className={cx('mt-1 size-3 shrink-0 text-subtle transition-transform', open && 'rotate-90')}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
        </svg>
        <span className="min-w-0 flex-1 leading-snug break-words">{topic.title}</span>
      </button>

      {open && (
        <div
          className="border-l border-line"
          style={{ marginLeft: `${depth * 12 + 13}px` }}
        >
          {docs.map((doc) => (
            <DocLink key={doc.id} doc={doc} depth={0} onNavigate={onNavigate} />
          ))}
          {children.map((child) => (
            <Topic key={child.slug} topic={child} depth={depth + 1} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  )
}

function DocLink({
  doc,
  depth,
  onNavigate,
}: {
  doc: TreeDoc
  depth: number
  onNavigate?: () => void
}) {
  const { pathById } = useDocsIndex()

  return (
    <NavLink
      // Link to the canonical path so a crawler never follows `/d/{id}`.
      to={pathById.get(doc.id) ?? `/d/${doc.id}`}
      onClick={onNavigate}
      title={doc.title}
      style={{ paddingLeft: `${depth * 12 + 12}px` }}
      className={({ isActive }) =>
        cx(
          'flex items-start gap-2 rounded-[var(--radius-sh)] py-1.5 pr-2 text-sm transition-colors',
          isActive
            ? 'bg-accent-soft font-medium text-accent-text'
            : 'text-muted hover:bg-sunken hover:text-ink',
        )
      }
    >
      {/* A doc title is often a whole route signature. Wrap it rather than cutting
          it to “All doctors (e…”, and shrink the internal marker to a dot so it
          never eats the words. */}
      <span className="min-w-0 flex-1 leading-snug break-words">{doc.title}</span>
      {doc.visibility === 'internal' && (
        <span
          title="Internal — staff only"
          aria-label="Internal"
          className="mt-1.5 size-1.5 shrink-0 rounded-full bg-internal"
        />
      )}
    </NavLink>
  )
}
